import packageInfo from '../../../package.json';
import appConfig from '../../configs/appConfig';
import awsRumActionTypes from '../../consts/aws/awsRumActionTypes';
import envVariables from '../../consts/envVariables';
import {selectUserGroups, selectUserId} from '../../state/selectors/auth';
import {selectIsPWA, selectIsRetail} from '../../state/selectors/global';
import {
    selectDeviceCodentify,
    selectDeviceConnectionType,
    selectDeviceModelType,
} from '../../state/selectors/iotDevices';
import {selectCurrentFlowParameters, selectPageParameters} from '../../state/selectors/monitoring';
import {
    selectInitialCodentify,
    selectInitialConnectionType,
    selectInitialDeviceType,
    selectQrCodentify,
} from '../../state/selectors/session';
import {getState} from '../../state/store';
import helpers from '../../utils/helpers';
import uaUtils from '../../utils/uaUtils';
import storageService from '../storageService';
import {userBrowserService} from '../userBrowserService';
import rumCustomEvents from './rumCustomEvents';

const APP_TYPE = 'NEXTGEN_WEB';

let isPushEventsPaused = false;
let isIntervalPaused = false;
let lastEventTime = 0;
let lastEventAction = null;
let firmwareUpdateParams = null;
let sessionId = null;
const hiddenIntervals = [];

const getSessionId = () => {
    if (!sessionId) {
        sessionId = storageService.getAnalyticsSessionId();
    }

    if (!sessionId) {
        sessionId = helpers.guid();
        storageService.setAnalyticsSessionId(sessionId);
    }

    return sessionId;
};

const resetSessionId = () => {
    sessionId = helpers.guid();
    storageService.setAnalyticsSessionId(sessionId);

    return sessionId;
};

const getDuration = (startTime) => new Date() - startTime;

const getAdjustedDuration = (startTime) => {
    const now = Date.now();

    let totalHiddenDuration = 0;

    hiddenIntervals.forEach((interval) => {
        if (interval.isPaused) return;

        if (interval.start > startTime) {
            const end = interval.end || now;

            totalHiddenDuration += end - interval.start;
        }
    });

    return now - startTime - totalHiddenDuration;
};

const collectAppData = () => {
    const state = getState();
    const userId = selectUserId(state);
    const isRetail = selectIsRetail(state);

    return {
        appType: APP_TYPE,
        env: envVariables.ENV,
        applicationVersion: packageInfo?.version,
        osName: uaUtils.getOSName(),
        osVersion: userBrowserService.getHighEntropyOSVersion() || uaUtils.getOSVersion(),
        market: appConfig.getMarket(),
        browserName: uaUtils.getBrowserName(),
        timestamp: Date.now(),
        sessionId: getSessionId(),
        targetGroups: selectUserGroups(state),
        isPWA: selectIsPWA(state),
        ...(userId && {userId}),
        ...(isRetail && {isAdmin: true}),
    };
};

const collectDeviceData = (serialNumber) => {
    if (!serialNumber) return {};

    return {
        serialNumber,
        codentify: selectDeviceCodentify(serialNumber)(getState()),
        connectionType: selectDeviceConnectionType(serialNumber)(getState()),
        deviceModel: selectDeviceModelType(serialNumber)(getState()),
    };
};

const collectPageData = () => selectPageParameters(getState());

const getStartParameters = () => ({startTime: new Date().getTime(), eventId: helpers.guid()});

const getStartParametersForCurrentFlow = () => selectCurrentFlowParameters(getState());

const collectInitialDeviceData = () => {
    const initialCodentify = selectQrCodentify(getState()) || selectInitialCodentify(getState());

    return {
        initialCodentify,
        initialConnectionType: selectInitialConnectionType(getState()),
        initialDeviceType: selectInitialDeviceType(getState()),
    };
};

const formatAction = (action, rumActionName = '') => {
    const actionName = String(rumActionName)
        .split('/')
        [String(rumActionName).split('/').length - 1].toUpperCase()
        .replaceAll('-', '_');

    return [action, actionName].filter(Boolean).join('_');
};

const onHidden = () => {
    const id = helpers.guid();

    rumCustomEvents.recordAction({eventId: id, action: awsRumActionTypes.HIDDEN});

    hiddenIntervals.push({id, start: Date.now(), end: null, isPaused: isIntervalPaused});
};

const onVisible = () => {
    const lastInterval = hiddenIntervals[hiddenIntervals?.length - 1];

    if (lastInterval && !lastInterval.end) {
        lastInterval.end = Date.now();
    }

    rumCustomEvents.recordAction({
        eventId: lastInterval?.id,
        action: awsRumActionTypes.VISIBLE,
        startTime: lastInterval?.start,
    });
};

const pauseHiddenEffect = () => (isIntervalPaused = true);
const resumeHiddenEffect = () => (isIntervalPaused = false);

const pausePushEvents = () => (isPushEventsPaused = true);
const resumePushEvents = () => (isPushEventsPaused = false);
const getIsPushEventsPaused = () => isPushEventsPaused;

const getLastEventData = () => ({
    timeSinceLastEvent: lastEventTime ? Date.now() - lastEventTime : 0,
    previousAction: lastEventAction,
});

const updateLastEventData = (action) => {
    lastEventTime = Date.now();
    lastEventAction = action;
};

const setFirmwareUpdateParams = () => (firmwareUpdateParams = getStartParameters());
const getFirmwareUpdateParams = () => firmwareUpdateParams;
const clearFirmwareUpdateParams = () => (firmwareUpdateParams = null);

export default {
    collectAppData,
    collectDeviceData,
    collectPageData,
    collectInitialDeviceData,
    getDuration,
    getAdjustedDuration,
    getStartParameters,
    getStartParametersForCurrentFlow,
    getSessionId,
    resetSessionId,
    formatAction,
    onHidden,
    onVisible,
    pauseHiddenEffect,
    resumeHiddenEffect,
    getLastEventData,
    updateLastEventData,
    pausePushEvents,
    resumePushEvents,
    getIsPushEventsPaused,
    setFirmwareUpdateParams,
    getFirmwareUpdateParams,
    clearFirmwareUpdateParams,
};
